import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitPractice } from '../services/practiceApi';
import {
  extractRequestId,
  buildResultPageState,
  clearPracticeStorage,
  buildSubmissionData,
  retryWithExponentialBackoff,
  getPracticeParams,
} from '../utils/practiceUtils';

/**
 * Custom hook for handling practice submission with retry and polling support
 * @param {Object} options - Configuration options
 * @param {Array} options.questions - Practice questions
 * @param {Object} options.sessionData - Session data from API
 * @param {Function} options.formatUserAnswers - Function to format user answers
 * @param {string} options.practiceType - Practice type ('speaking-practice', 'writing-practice', etc.)
 * @param {Function} options.getSubpracticeType - Function to determine subpractice type (optional)
 * @param {Function} options.stopPolling - Function to stop polling (optional)
 * @param {Function} options.startPolling - Function to start polling (optional)
 * @param {Function} options.setRequestId - Function to set request ID for polling (optional)
 * @param {Function} options.onSyncSuccess - Callback for synchronous submission success (optional)
 * @param {Function} options.shouldRetry - Function to determine if should retry (optional, for pronunciation)
 * @returns {Object} { handleSubmit, isSubmitting, submittedAnswers }
 */
export const usePracticeSubmission = ({
  questions,
  sessionData,
  formatUserAnswers,
  practiceType,
  getSubpracticeType,
  stopPolling,
  startPolling,
  setRequestId,
  onSyncSuccess,
  shouldRetry = () => true, // Default: always retry
  // changed by Cursor: callback so parent can show error bar + "Try again" instead of dead-end alert
  onSubmitError,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedAnswers, setSubmittedAnswers] = useState([]);

  // changed by Cursor: allow parent to reset submitting when status API returns error (e.g. so overlay hides and user can try again)
  const resetSubmitting = useCallback(() => {
    setIsSubmitting(false);
  }, []);

  const handleSubmit = useCallback(async (answers) => {
    if (!sessionData) {
      alert('Session data is missing. Please try again.');
      return;
    }

    setSubmittedAnswers(answers);
    setIsSubmitting(true);

    try {
      const userAnswers = formatUserAnswers(questions, answers);
      const practiceParams = getPracticeParams(location);
      const subpracticeType = getSubpracticeType ? getSubpracticeType(questions) : null;

      const { submissionData, error } = buildSubmissionData({
        sessionData,
        practiceType,
        subpracticeType,
        practiceParams,
        userAnswers,
      });

      if (error) {
        setIsSubmitting(false);
        if (onSubmitError) { onSubmitError(error); } else { alert(error); }
        return;
      }

      const BASE_DELAY = 2000;
      const MAX_DELAY = 30000;
      const MAX_RETRIES = 10;
      let result = null;
      let attemptCount = 0;
      const canRetry = shouldRetry(questions);

      while (attemptCount < MAX_RETRIES) {
        try {
          attemptCount++;
          result = await submitPractice(submissionData);

          if (result && result.success && result.status !== 'error') {
            const { requestId, isValid: isValidRequestId, trimmed } = extractRequestId(result);

            // Synchronous submission
            if (result.data && !isValidRequestId) {
              if (stopPolling) stopPolling();
              setIsSubmitting(false);
              clearPracticeStorage(sessionData['session-id'], practiceType);

              const results = buildResultPageState({
                sessionData,
                courseType: practiceType === 'speaking-practice' ? 'Speaking Practice' : 
                           practiceType === 'writing-practice' ? 'Writing Practice' : 'Quick Practice',
                practiceParams,
                apiResponse: result.data,
                originalQuestions: questions,
                userAnswers: answers,
                fromPage: location.state?.fromPage || '/learner/language-learning',
              });

              if (onSyncSuccess) {
                onSyncSuccess(results);
              } else {
                navigate('/learner/result', { state: results });
              }
              return;
            }

            // Async submission - need request-id
            if (!isValidRequestId) {
              if (attemptCount >= MAX_RETRIES) {
                setIsSubmitting(false);
                if (onSubmitError) { onSubmitError('Failed to submit. Try again'); } else { alert(`Failed after ${MAX_RETRIES} attempts. Please try again later.`); }
                return;
              }
              const delay = Math.min(BASE_DELAY * Math.pow(2, attemptCount - 1), MAX_DELAY);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }

            // Validate pronunciation shouldn't have request-id
            if (!canRetry && isValidRequestId) {
              if (process.env.NODE_ENV === 'development') {
                console.error('⚠️ Pronunciation received request-id, this should not happen');
              }
              setIsSubmitting(false);
              alert('Unexpected error: Pronunciation should not use async processing.');
              return;
            }

            clearPracticeStorage(sessionData['session-id'], practiceType);
            if (setRequestId) setRequestId(trimmed);
            if (startPolling) startPolling();
            break;
          }

          // Error handling
          // Check if error is about learner-id - don't retry if init failed
          const errorMessage = result?.message || '';
          const errorMessageLower = errorMessage.toLowerCase();
          const isLearnerIdError = errorMessageLower.includes('learner-id') || 
                                   errorMessageLower.includes('learner id') ||
                                   errorMessageLower.includes('learner-id is required') ||
                                   errorMessageLower.includes('learner-id required') ||
                                   errorMessageLower.includes('learner-id missing') ||
                                   errorMessageLower.includes('must provide learner-id');
          
          if (isLearnerIdError) {
            // Learner-id error - interceptor will handle redirect, just stop here
            setIsSubmitting(false);
            if (stopPolling) stopPolling();
            console.log('❌ Submit failed due to learner-id error, stopping retry');
            return;
          }

          if (!canRetry) {
            setIsSubmitting(false);
            if (onSubmitError) { onSubmitError(result?.message || 'Failed to submit. Try again'); } else { alert(result?.message || 'Failed to submit. Please try again.'); }
            return;
          }

          if (attemptCount >= MAX_RETRIES) {
            setIsSubmitting(false);
            if (onSubmitError) { onSubmitError('Failed to submit. Try again'); } else { alert(`Failed after ${MAX_RETRIES} attempts. Please try again later.`); }
            return;
          }

          const delay = Math.min(BASE_DELAY * Math.pow(2, attemptCount - 1), MAX_DELAY);
          await new Promise(resolve => setTimeout(resolve, delay));
        } catch (error) {
          // Check if error is about learner-id - don't retry if init failed
          const errorMessage = error?.message || '';
          const errorMessageLower = errorMessage.toLowerCase();
          const isLearnerIdError = errorMessageLower.includes('learner-id') || 
                                   errorMessageLower.includes('learner id') ||
                                   errorMessageLower.includes('learner-id is required') ||
                                   errorMessageLower.includes('learner-id required') ||
                                   errorMessageLower.includes('learner-id missing') ||
                                   errorMessageLower.includes('must provide learner-id');
          
          if (isLearnerIdError) {
            // Learner-id error - interceptor will handle redirect, just stop here
            setIsSubmitting(false);
            if (stopPolling) stopPolling();
            console.log('❌ Submit failed due to learner-id error, stopping retry');
            return;
          }

          if (attemptCount >= MAX_RETRIES) {
            if (process.env.NODE_ENV === 'development') {
              console.error(`❌ Max retries reached (${MAX_RETRIES}):`, error);
            }
            setIsSubmitting(false);
            if (onSubmitError) { onSubmitError('Failed to submit. Try again'); } else { alert(`Failed after ${MAX_RETRIES} attempts. Please try again later.`); }
            return;
          }

          if (!canRetry) {
            setIsSubmitting(false);
            if (onSubmitError) { onSubmitError(error.message || 'Failed to submit. Try again'); } else { alert(error.message || 'Failed to submit. Please try again.'); }
            return;
          }

          const delay = Math.min(BASE_DELAY * Math.pow(2, attemptCount - 1), MAX_DELAY);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      if (!result || !result.success) {
        setIsSubmitting(false);
        if (onSubmitError) { onSubmitError('Failed to submit. Try again'); } else { alert('Failed to submit practice after multiple attempts. Please try again later.'); }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Unexpected error in submission:', error);
      }
      setIsSubmitting(false);
    }
  }, [
    questions,
    sessionData,
    formatUserAnswers,
    practiceType,
    getSubpracticeType,
    location,
    navigate,
    stopPolling,
    startPolling,
    setRequestId,
    onSyncSuccess,
    shouldRetry,
    onSubmitError,
  ]);

  return {
    handleSubmit,
    isSubmitting,
    submittedAnswers,
    resetSubmitting,
  };
};

/**
 * Custom hook for handling polling result and navigation.
 *
 * Async grading contract (two-layer polling):
 *   200 + status:"processing" → keep polling
 *   200 + status:"ok"         → stop polling, navigate to result
 *   200 + status:"error"      → retryable, keep polling (show retry count after threshold)
 *   500 + status:"error"      → fatal BE error, stop polling, show error
 *
 * @param {Function} onPollingRetry - callback(count) called on each retryable error; count=0 resets
 * @param {number}   maxPollingErrorRetries - consecutive 200+error polls before giving up (default 40)
 */
export const usePollingResult = ({
  statusData,
  isSubmitting,
  stopPolling,
  sessionData,
  questions,
  submittedAnswers,
  practiceType,
  location,
  navigate,
  onSubmissionError,
  onGradingSuccess,
  onPollingRetry,
  maxPollingErrorRetries = 40,
}) => {
  const pollingErrorCountRef = useRef(0);

  useEffect(() => {
    if (!statusData || !isSubmitting) return;

    if (statusData.status === 'ok' && statusData.data) {
      pollingErrorCountRef.current = 0;
      if (onPollingRetry) onPollingRetry(0);
      stopPolling();

      if (onGradingSuccess) onGradingSuccess();

      const practiceParams = getPracticeParams(location);
      const results = buildResultPageState({
        sessionData,
        courseType: practiceType === 'speaking-practice' ? 'Speaking Practice' : 'Writing Practice',
        practiceParams,
        apiResponse: statusData.data,
        originalQuestions: questions,
        userAnswers: submittedAnswers,
        fromPage: location.state?.fromPage || '/learner/language-learning',
      });

      navigate('/learner/result', { state: results });
    } else if (statusData.status === 'error' && statusData.httpStatus >= 500) {
      pollingErrorCountRef.current = 0;
      stopPolling();
      if (onSubmissionError) {
        onSubmissionError({
          message: statusData.message || 'Server error. Please try again later.',
          kind: 'poll_http_500',
        });
      }
    } else if (statusData.status === 'error') {
      // 200 + status:"error" → retryable, but track consecutive failures
      pollingErrorCountRef.current += 1;
      const count = pollingErrorCountRef.current;

      if (onPollingRetry) onPollingRetry(count);

      if (count >= maxPollingErrorRetries) {
        pollingErrorCountRef.current = 0;
        stopPolling();
        if (onSubmissionError) {
          onSubmissionError({
            message:
              statusData.message || 'Grading failed after multiple retries. Please try again.',
            kind: 'poll_max_retries',
          });
        }
      }
    } else if (statusData.status === 'processing') {
      // Reset error counter when BE is actively processing
      if (pollingErrorCountRef.current > 0) {
        pollingErrorCountRef.current = 0;
        if (onPollingRetry) onPollingRetry(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData, isSubmitting, stopPolling, sessionData, questions, submittedAnswers, practiceType, location, navigate, onSubmissionError, onPollingRetry, maxPollingErrorRetries]);
};

